/*
 * Último recurso del cold-start: recarga amigable (loop-safe) cuando las cargas críticas de
 * arranque siguen fallando tras los reintentos transparentes del interceptor de `axiosInstance`.
 *
 * Contexto (ver `authReady.ts`): en frío, la avalancha de mount-fetches throttlea al authorizer
 * del panel → 429/500 en ráfaga. El retry con backoff cubre el caso típico; este módulo es la red
 * de seguridad para el cold-start REALMENTE atascado. Estrategia, en orden:
 *
 *   1. Primer atasco de arranque → aviso breve NO técnico + UN `window.location.reload()`,
 *      guardado contra loops con `sessionStorage` (`hasColdStartAutoReloaded`). El cold-start casi
 *      siempre se resuelve al segundo intento (authorizer ya caliente).
 *   2. Si YA recargamos una vez y sigue fallando → NO recargar en loop. Mostrar un prompt manual
 *      persistente con botón "Recargar". Texto humano, sin códigos (429/500/authorizer/token).
 *
 * El reload SOLO aplica al escenario de arranque (`isInStartupWindow`): un 500 puntual en runtime
 * NO recarga (sería molesto) — eso lo decide el caller (interceptor) antes de invocar aquí.
 *
 * El prompt manual se pinta con DOM crudo (no React) a propósito: debe verse aunque el árbol de
 * React no haya montado bien por el fallo de arranque. El aviso breve usa `react-hot-toast`, que
 * ya está montado globalmente en `App.tsx`.
 */
import toast from 'react-hot-toast';
import { hasColdStartAutoReloaded, markColdStartAutoReloaded } from './authReady';

// Copy 100% humano — NUNCA exponer 429/500/authorizer/token/códigos al usuario.
const RELOADING_MESSAGE = 'Estamos preparando tu panel, recargando…';
const MANUAL_PROMPT_MESSAGE = 'Necesitamos recargar la página para continuar.';
const MANUAL_PROMPT_BUTTON = 'Recargar';
const MANUAL_PROMPT_DISMISS = 'Seguir sin recargar';

const OVERLAY_ID = 'cold-start-reload-overlay';

// Guard de proceso: una vez que decidimos recargar / mostrar el prompt en esta carga de página,
// no repetimos la acción aunque lleguen más fallos de arranque en la misma ráfaga.
let actionTaken = false;

const doReload = (): void => {
  try {
    window.location.reload();
  } catch {
    // Si reload no está disponible (entorno raro), no hay más que hacer; el prompt manual
    // seguirá visible si llegamos a mostrarlo.
  }
};

/**
 * Pinta un overlay persistente NO técnico con un botón "Recargar". DOM crudo para que se vea
 * aunque la app de React no haya montado. Idempotente: si ya existe, no duplica.
 *
 * Es DESCARTABLE a propósito: el atasco de arranque casi nunca deja la app inservible — lo normal
 * es que el panel haya montado y solo falten algunas cargas. Un modal sin salida sobre una app que
 * sí responde deja al comercio sin poder atender (ni siquiera ver el toast de un pedido entrante,
 * que va por debajo de este overlay). El botón secundario cierra el overlay sin recargar; la
 * decisión queda en el operador.
 */
const showManualReloadPrompt = (): void => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(OVERLAY_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-live', 'assertive');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:rgba(37,55,70,0.55)',
    'font-family:Poppins,system-ui,sans-serif',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'background:#ffffff',
    'border-radius:12px',
    'padding:24px',
    'max-width:360px',
    'width:calc(100% - 48px)',
    'box-shadow:0 8px 24px rgba(0,0,0,0.18)',
    'text-align:center',
  ].join(';');

  const text = document.createElement('p');
  text.textContent = MANUAL_PROMPT_MESSAGE;
  text.style.cssText = ['margin:0 0 20px', 'color:#253746', 'font-size:16px', 'line-height:1.4'].join(';');

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = MANUAL_PROMPT_BUTTON;
  button.style.cssText = [
    'background:#4298B5',
    'color:#ffffff',
    'border:none',
    'border-radius:8px',
    'padding:12px 24px',
    'font-size:14px',
    'font-weight:700',
    'text-transform:uppercase',
    'cursor:pointer',
    'font-family:inherit',
  ].join(';');
  button.addEventListener('click', doReload);

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = MANUAL_PROMPT_DISMISS;
  dismiss.style.cssText = [
    'display:block',
    'margin:16px auto 0',
    'background:none',
    'border:none',
    'padding:8px',
    'color:#5A6B78',
    'font-size:13px',
    'text-decoration:underline',
    'cursor:pointer',
    'font-family:inherit',
  ].join(';');
  dismiss.addEventListener('click', () => {
    overlay.remove();
  });

  card.appendChild(text);
  card.appendChild(button);
  card.appendChild(dismiss);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
};

/**
 * Gestiona el último recurso del cold-start atascado (loop-safe). Llamar SOLO cuando una carga
 * crítica de arranque agotó los reintentos DENTRO de la ventana de arranque.
 *
 *   - Primera vez (no recargado aún): aviso breve no técnico + reload (marcado en sessionStorage).
 *   - Ya recargado una vez: NO recargar en loop → prompt manual persistente.
 *
 * Idempotente dentro de la misma carga de página (`actionTaken`): no dispara dos reloads ni dos
 * prompts por la misma ráfaga de fallos.
 */
export const handleStuckColdStart = (): void => {
  if (actionTaken) return;
  actionTaken = true;

  if (hasColdStartAutoReloaded()) {
    // Ya recargamos una vez y sigue fallando: cortamos el loop y pedimos acción manual.
    showManualReloadPrompt();
    return;
  }

  // Primer atasco: avisamos en humano y recargamos UNA vez (el cold-start suele ceder al 2º intento).
  // Fail-safe (hallazgo Seer): solo recargamos si pudimos PERSISTIR el guard. Sin sessionStorage
  // (modo privado) el flag no sobreviviría al reload → loop infinito. En ese caso NO recargamos:
  // vamos directo al prompt manual para que el usuario decida.
  if (!markColdStartAutoReloaded()) {
    showManualReloadPrompt();
    return;
  }
  try {
    toast.loading(RELOADING_MESSAGE, { id: OVERLAY_ID, duration: 4000 });
  } catch {
    // El toast es un "nice to have"; si falla, igual recargamos.
  }
  // Pequeño respiro para que el aviso alcance a pintarse antes de recargar.
  setTimeout(doReload, 600);
};

// Solo para tests: reinicia el guard de proceso entre casos.
export const __resetColdStartReloadForTests = (): void => {
  actionTaken = false;
  if (typeof document !== 'undefined') {
    document.getElementById(OVERLAY_ID)?.remove();
  }
};
